<script setup>
import { ref, computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import QrcodeVue from "qrcode.vue";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";

const props = defineProps({
  transactionUuid: {
    type: String,
    default: "",
  },
  participantUuid: {
    type: String,
    default: "",
  },
  participantName: {
    type: String,
    default: "",
  },
  closeSignal: {
    type: Number,
    default: 0,
  },
  closeTargetUuid: {
    type: String,
    default: "",
  },
});

const { locale } = useI18n();

const open = ref(false);

const signLink = computed(() => {
  if (!props.transactionUuid || !props.participantUuid) return "";
  const baseDomain =
    window.__CONFIG__?.VUE_APP_BASE_DOMAIN || "https://notary.q19.kz";
  const params = new URLSearchParams({
    transaction_uuid: props.transactionUuid,
    participant_uuid: props.participantUuid,
    lan: locale.value || "ru",
  });
  return `${baseDomain}/tg/mgovSign?${params.toString()}`;
});

const qrValue = computed(() =>
  signLink.value ? `mobileSign:${signLink.value}` : ""
);

function openDialog() {
  if (!qrValue.value) return;
  open.value = true;
}

watch(
  () => props.closeSignal,
  () => {
    if (!open.value) return;
    if (
      !props.closeTargetUuid ||
      props.closeTargetUuid === props.participantUuid
    ) {
      open.value = false;
    }
  }
);
</script>

<template>
  <button
    type="button"
    class="egov-chip"
    :disabled="!qrValue"
    title="eGov mobile QR"
    @click.stop="openDialog"
  >
    <svg-icon name="qr-code" class="h-7 w-7 shrink-0" />
  </button>

  <Dialog v-model:open="open">
    <DialogContent class="sm:max-w-[360px]">
      <DialogHeader>
        <DialogTitle>{{ $t("Подписание через eGov mobile") }}</DialogTitle>
      </DialogHeader>
      <div class="qr-block">
        <qrcode-vue v-if="qrValue" :value="qrValue" :size="240" level="H" />
        <div v-if="participantName" class="qr-name">
          {{ participantName }}
        </div>
        <div v-if="signLink" class="qr-hint">
          {{
            $t(
              "Отсканируйте QR-код в приложении eGov mobile. Статус обновится после подписания."
            )
          }}
        </div>
      </div>
    </DialogContent>
  </Dialog>
</template>

<style scoped>
.egov-chip {
  @apply inline-flex items-center justify-center rounded-xl border border-border bg-card h-[50px] w-[50px] text-foreground transition-colors;
}

.egov-chip:hover:not(:disabled) {
  background-color: hsl(var(--muted));
}

.egov-chip:disabled {
  @apply cursor-not-allowed opacity-50;
}

.qr-block {
  @apply w-full flex flex-col items-center gap-3 py-4 px-4 rounded-md;
  background-color: hsl(var(--background));
  border: 1px solid hsl(var(--border));
}

.qr-name {
  @apply text-sm font-medium text-foreground text-center;
}

.qr-hint {
  @apply text-xs text-muted-foreground text-center max-w-[260px];
}
</style>
